import { Button, Tooltip, Typography } from "@povio/ui";
import Image from "next/image";
import { useCallback } from "react";

import { InfoIcon } from "@/assets/icons/general/Info";
import { CopyIcon } from "@/assets/icons/ui/Copy";
import BoneBadgeImage from "@/assets/images/rewards/bone-badge.png";
import { RewardImage } from "@/components/loyalty/RewardImage";
import { ModalSheet } from "@/components/shared/modal/ModalSheet";
import CustomTooltipTrigger from "@/components/ui/overlays/Tooltip/CustomTooltipTrigger";
import { AuthContext } from "@/data/auth/auth.context";
import { CommonModels } from "@/data/common/common.models";
import { useToast } from "@/hooks/useToast";
import { useOverlayQueueStore } from "@/providers/overlay/overlay-queue.store";
import type { UnlockedRewardData } from "@/types/loyalty.types";
import { DateUtils } from "@/util/date.utils";

const ModalContent = ({ unlockedReward }: { unlockedReward: UnlockedRewardData }) => {
  const { successToast } = useToast();

  const handleCopyCode = useCallback(async () => {
    if (unlockedReward?.redeemLink) {
      await navigator.clipboard.writeText(unlockedReward.redeemLink);
      successToast({ text: "Link successfully copied." });
    } else if (unlockedReward?.redeemCode) {
      await navigator.clipboard.writeText(unlockedReward.redeemCode);
      successToast({ text: "Code successfully copied." });
    }
  }, [unlockedReward?.redeemLink, unlockedReward?.redeemCode, successToast]);

  return (
    <div className="flex w-full flex-col gap-2 pt-2 pb-6">
      {/* Reward Image */}
      <div className="flex justify-center px-6 pt-7 pb-2">
        <RewardImage
          isUnlocked={true}
          imageUrl={unlockedReward.imageUrl ?? undefined}
          unlockedImageUrl={unlockedReward.unlockedImageUrl ?? undefined}
          className="h-[180px] w-[180px]"
        />
      </div>

      {unlockedReward.unlockedAt && (
        <div className="flex flex-col items-center gap-2 pb-4">
          <Typography
            size="body-4"
            className="text-text-default-1"
          >
            Unlocked on {DateUtils.getLocalizedDate(unlockedReward.unlockedAt, "MMM d, yyyy")}
          </Typography>
        </div>
      )}

      {/* Claim Code */}
      {unlockedReward.redeemCode ? (
        <div className="flex flex-col items-center gap-2 py-2">
          <Typography
            size="body-4"
            className="text-text-default-2"
          >
            Claim code:
          </Typography>
          <Button
            size="s"
            color="secondary"
            variant="contained"
            onPress={handleCopyCode}
            icon={CopyIcon}
            iconPosition="right"
            iconClassName="h-6 w-6"
          >
            {unlockedReward.redeemCode}
          </Button>
          <div className="mt-4 flex flex-col gap-2">
            <Tooltip text="This coupon can only be used once for a single purchase and can't be split across multiple purchases.">
              <CustomTooltipTrigger>
                <div className="flex items-center gap-1 text-text-default-2">
                  <InfoIcon className="h-4 w-4" />
                  <Typography
                    variant="default"
                    size="body-4"
                  >
                    Coupon can only be used once for a single purchase.
                  </Typography>
                </div>
              </CustomTooltipTrigger>
            </Tooltip>
            <Tooltip text="This code will be applied during checkout and will not be visible in earlier pages.">
              <CustomTooltipTrigger>
                <div className="flex items-center gap-1 text-text-default-2">
                  <InfoIcon className="h-4 w-4" />
                  <Typography
                    variant="default"
                    size="body-4"
                  >
                    Code will be applied at checkout.
                  </Typography>
                </div>
              </CustomTooltipTrigger>
            </Tooltip>
          </div>
        </div>
      ) : (
        <Tooltip text="Please check again later or contact support.">
          <CustomTooltipTrigger>
            <div className="flex items-center justify-center gap-2 py-2 text-text-default-2">
              <InfoIcon className="h-4 w-4" />
              <Typography
                size="body-4"
                className="text-text-default-2"
              >
                Coupon code is not generated.
              </Typography>
            </div>
          </CustomTooltipTrigger>
        </Tooltip>
      )}

      {/* Description */}
      <div className="flex justify-center pb-8">
        <Typography
          size="body-4"
          className="max-w-[432px] text-center text-text-default-2"
        >
          <p
            className="loyalty-description"
            dangerouslySetInnerHTML={{ __html: unlockedReward.description }}
          ></p>
        </Typography>
      </div>
    </div>
  );
};

export const LoyaltyModal = () => {
  const { isLoggedIn, user } = AuthContext.useAuth();
  const { currentOverlay, dequeueOverlay } = useOverlayQueueStore();

  // Get loyalty modal data from queue
  const isLoyaltyModalOpen = currentOverlay?.type === "loyalty-reward";
  const unlockedReward = currentOverlay?.data as UnlockedRewardData | null;

  // Handle close
  const handleClose = useCallback(() => {
    dequeueOverlay();
  }, [dequeueOverlay]);

  // Check if user can see the modal
  const isOnboardingCompleted = user?.onboardingStep === CommonModels.OnboardingStepEnum.completed;

  // Don't render if user is not authenticated/onboarded
  if (!isLoggedIn || !isOnboardingCompleted) {
    return null;
  }

  if (!unlockedReward || !isLoyaltyModalOpen) return null;

  return (
    <ModalSheet
      isOpen={isLoyaltyModalOpen}
      onClose={handleClose}
      icon={
        <Image
          src={BoneBadgeImage.src}
          alt="Bone Badge"
          width={26}
          height={26}
        />
      }
      title={unlockedReward.name}
      titleClassName="text-[20px]! text-text-default-1"
      modalClassName="bg-elevation-fill-default-2!"
      fitContent
    >
      <ModalContent unlockedReward={unlockedReward} />
    </ModalSheet>
  );
};
